"use client";

import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import CaptchaField from "./CaptchaField";
import { requiresCaptcha } from "@/libs/captcha-policy";

import {
  registerCaptchaPrompt,
  serializeCaptchaSubmission,
} from "@/libs/captcha-client";

type Challenge = {
  id: number;
  resolve: (answer: string | null) => void;
  error?: string;
};

// Public submission routes share one dialog when no inline CAPTCHA is supplied.
// Requests are serialized because the existing CAPTCHA uses a browser-wide cookie.
export default function CaptchaSubmissionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [answer, setAnswer] = useState("");
  const dialog = useRef<HTMLDialogElement>(null);
  const sequence = useRef(0);
  const pending = useRef<Challenge | null>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const pathname = usePathname();
  const fa = pathname?.startsWith("/fa/") ?? false;

  useLayoutEffect(() => {
    if (challenge) {
      previousFocus.current = document.activeElement as HTMLElement | null;
      dialog.current?.showModal();
    } else {
      dialog.current?.close();
      previousFocus.current?.focus();
    }
  }, [challenge]);

  useLayoutEffect(() => {
    const original = window.fetch;
    let active = true;
    const ask = (error?: string) =>
      new Promise<string | null>((resolve) => {
        if (!active) return resolve(null);
        const next = { id: ++sequence.current, resolve, error };
        pending.current = next;
        setAnswer("");
        setChallenge(next);
      });
    registerCaptchaPrompt(ask);
    const intercepted: typeof fetch = (input, init) => {
      const url = new URL(
        input instanceof Request ? input.url : String(input),
        window.location.href,
      );
      const method =
        init?.method ?? (input instanceof Request ? input.method : "GET");
      if (
        url.origin !== window.location.origin ||
        !requiresCaptcha(url.pathname, method)
      )
        return original(input, init);
      const request = new Request(input instanceof Request ? input : url, init);
      // Auth forms already have a visible CAPTCHA. Reuse their answer.
      let supplied = request.headers.get("x-captcha-answer") ?? "";
      if (init?.body instanceof FormData)
        supplied ||= String(init.body.get("captcha") ?? "");
      else if (typeof init?.body === "string") {
        try {
          supplied ||= JSON.parse(init.body).captcha ?? "";
        } catch {
          /* Non-JSON bodies use the dialog. */
        }
      }
      const run = async () => {
        request.signal.throwIfAborted();
        let value = supplied || (await ask());
        while (value && active) {
          request.signal.throwIfAborted();
          const headers = new Headers(request.headers);
          headers.set("x-captcha-answer", value);
          const response = await original(
            new Request(request.clone(), { headers }),
          );
          if (response.headers.get("x-captcha-required") !== "1")
            return response;
          // The server rejected the challenge before the mutation handler ran.
          value = await ask(
            fa
              ? "کد نادرست یا منقضی شده است."
              : "The code is incorrect or expired.",
          );
        }
        return Response.json(
          {
            error: fa ? "ارسال لغو شد." : "Submission cancelled.",
            message: "Submission cancelled.",
          },
          { status: 400 },
        );
      };
      return serializeCaptchaSubmission(run);
    };
    window.fetch = intercepted;
    return () => {
      active = false;
      if (window.fetch === intercepted) window.fetch = original;
      registerCaptchaPrompt(null);
      pending.current?.resolve(null);
      pending.current = null;
      setChallenge(null);
    };
  }, [fa]);

  const finish = (value: string | null) => {
    pending.current?.resolve(value);
    pending.current = null;
    setChallenge(null);
  };

  return (
    <>
      {children}
      <dialog
        ref={dialog}
        aria-labelledby="captcha-submission-title"
        onCancel={(event) => {
          event.preventDefault();
          finish(null);
        }}
        className="m-auto w-[min(92vw,400px)] rounded-xl border border-gray-200 bg-white p-5 shadow-xl backdrop:bg-black/40"
        dir={fa ? "rtl" : "ltr"}
      >
        {challenge && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (answer.trim()) finish(answer.trim());
            }}
            className="space-y-4"
          >
            <h2 id="captcha-submission-title" className="text-lg font-semibold">
              {fa ? "تأیید ارسال" : "Verify submission"}
            </h2>
            <CaptchaField
              key={challenge.id}
              autoFocus
              value={answer}
              onChange={setAnswer}
              error={challenge.error}
              placeholder={fa ? "کد تصویر را وارد کنید" : "Enter captcha"}
              refreshLabel={fa ? "تصویر جدید" : "Refresh captcha"}
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => finish(null)}
                className="rounded border px-3 py-2"
              >
                {fa ? "لغو" : "Cancel"}
              </button>
              <button
                type="submit"
                disabled={!answer.trim()}
                className="rounded bg-slate-800 px-3 py-2 text-white disabled:opacity-50"
              >
                {fa ? "ارسال" : "Submit"}
              </button>
            </div>
          </form>
        )}
      </dialog>
    </>
  );
}
